import { useEffect, useState, useCallback, useRef } from "react";
import api, { errMsg } from "../lib/api";
import { fmtIDR, fmtDate, today } from "../lib/format";
import { PageHeader, Card, Badge, Modal, Field, Btn, DataTable, inputCls, statusTone } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { Plus, Banknote, CalendarClock, Upload } from "lucide-react";
import { toast } from "sonner";
import SiteplanMap from "../components/SiteplanMap";

export default function Penjualan() {
  const { hasPerm, projects, projectId } = useAuth();
  const [tab, setTab] = useState("siteplan");
  const [units, setUnits] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [akunRes, setAkunRes] = useState(null);
  const [showBerkas, setShowBerkas] = useState(null);
  const setProcess = async (bid, stage) => {
    try {
      const r = await api.put(`/sales/bookings/${bid}/process-stage`, { stage });
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const uploadBerkas = (docType, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const r = await api.post(`/sales/bookings/${showBerkas.id}/berkas`, { doc_type: docType, file: reader.result });
        toast.success(r.data.message);
        const res = await api.get(`/sales/bookings`);
        const updated = res.data.find((x) => x.id === showBerkas.id);
        setShowBerkas(updated || showBerkas);
        load();
      } catch (err) { toast.error(errMsg(err)); }
    };
    reader.readAsDataURL(file);
  };
  const createAkun = async (r) => {
    try {
      const res = await api.post(`/sales/bookings/${r.id}/customer-account`, {});
      setAkunRes({ ...res.data, customer: r.customer_name });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const [payments, setPayments] = useState([]);
  const [filter, setFilter] = useState("semua");
  const [selected, setSelected] = useState(null);
  const [unitEditForm, setUnitEditForm] = useState({ number: "", type: "", price: 0, status: "tersedia", luas_tanah: 0, luas_bangunan: 0 });
  const [showBooking, setShowBooking] = useState(false);
  const [showPay, setShowPay] = useState(null);
  const [showUnit, setShowUnit] = useState(false);
  const [bk, setBk] = useState({ customer_name: "", customer_phone: "", price: 0, booking_fee: 0, dp: 0, payment_method: "cash", schedules: [] });
  const [pay, setPay] = useState({ date: today(), amount: 0, kind: "booking_fee", schedule_idx: null, method: "transfer" });
  const [discountPct, setDiscountPct] = useState(0);
  const [unitForm, setUnitForm] = useState({ location_id: "", block_id: "", number: "", type: "36/72", luas_tanah: 72, luas_bangunan: 36, bedrooms: 2, bathrooms: 1, price: 0, status: "tersedia" });
  const [petaProject, setPetaProject] = useState("");
  const [petaDetail, setPetaDetail] = useState(null);
  const [petaBlocks, setPetaBlocks] = useState([]);
  const [petaBlockId, setPetaBlockId] = useState("");
  const [petaBlockDetail, setPetaBlockDetail] = useState(null);
  const [unitBlocks, setUnitBlocks] = useState([]);
  const siteplanFileRef = useRef(null);
  const locPhotoRef = useRef(null);
  const [locInfo, setLocInfo] = useState(null);

  const openLocInfo = () => {
    if (!petaDetail) return toast.error("Pilih lokasi proyek terlebih dahulu");
    setLocInfo({ description: petaDetail.description || "", facilities: (petaDetail.facilities || []).join(", "),
      nearby: petaDetail.nearby || [], lat: petaDetail.geofence_lat ?? "", lng: petaDetail.geofence_lng ?? "",
      images: petaDetail.images || [] });
  };

  const saveLocInfo = async () => {
    try {
      await api.put(`/locations/${petaDetail.id}`, { name: petaDetail.name, code: petaDetail.code || "", address: petaDetail.address || "",
        geofence_lat: locInfo.lat === "" ? null : +locInfo.lat, geofence_lng: locInfo.lng === "" ? null : +locInfo.lng,
        geofence_radius_m: petaDetail.geofence_radius_m || 200, manager_id: petaDetail.manager_id || null, mandor_id: petaDetail.mandor_id || null,
        description: locInfo.description, facilities: locInfo.facilities.split(",").map((s) => s.trim()).filter(Boolean),
        nearby: locInfo.nearby.filter((n) => n.name && n.name.trim()), images: locInfo.images });
      toast.success("Info proyek tersimpan — tampil di katalog aplikasi ABAD JAYA LIVING");
      setLocInfo(null);
      const r = await api.get(`/locations/${petaDetail.id}`);
      setPetaDetail(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const uploadLocPhotos = async (e) => {
    const files = [...e.target.files].slice(0, 8);
    if (!files.length || !petaDetail) return;
    const fd = new FormData();
    files.forEach((f) => fd.append("photos", f));
    try {
      await api.post(`/locations/${petaDetail.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto proyek terunggah");
      const r = await api.get(`/locations/${petaDetail.id}`);
      setPetaDetail(r.data);
      if (locInfo) setLocInfo({ ...locInfo, images: r.data.images || [] });
    } catch (err) { toast.error(errMsg(err)); }
  };

  const load = useCallback(() => {
    const q = projectId ? `?project_id=${projectId}` : "";
    api.get(`/sales/units${q}`).then((r) => setUnits(r.data));
    api.get(`/sales/bookings${q}`).then((r) => setBookings(r.data));
    api.get("/sales/payments").then((r) => setPayments(r.data));
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const activePetaProject = petaProject || projectId || projects[0]?.id || "";
  useEffect(() => {
    if (activePetaProject) {
      api.get(`/locations/${activePetaProject}`).then((r) => setPetaDetail(r.data)).catch(() => setPetaDetail(null));
      api.get(`/locations/${activePetaProject}/blocks`).then((r) => setPetaBlocks(r.data)).catch(() => {});
    }
  }, [activePetaProject]);
  useEffect(() => {
    if (petaBlockId) api.get(`/blocks/${petaBlockId}`).then((r) => setPetaBlockDetail(r.data)).catch(() => setPetaBlockDetail(null));
    else setPetaBlockDetail(null);
  }, [petaBlockId]);

  const petaImage = petaBlockDetail?.siteplan_image || petaDetail?.siteplan_image;

  const selectUnit = (u) => {
    setSelected(u);
    setUnitEditForm({ number: u.number, type: u.type, price: u.price, status: u.status, luas_tanah: u.luas_tanah, luas_bangunan: u.luas_bangunan, bedrooms: u.bedrooms ?? 2, bathrooms: u.bathrooms ?? 1 });
  };

  const saveUnitEdit = async () => {
    const u = selected;
    try {
      await api.put(`/sales/units/${u.id}`, { block: u.block, number: unitEditForm.number, type: unitEditForm.type,
        luas_tanah: +unitEditForm.luas_tanah, luas_bangunan: +unitEditForm.luas_bangunan, bedrooms: +unitEditForm.bedrooms, bathrooms: +unitEditForm.bathrooms, price: +unitEditForm.price,
        status: unitEditForm.status, project_id: u.project_id, location_id: u.location_id, block_id: u.block_id, polygon: u.polygon || [] });
      toast.success("Unit diperbarui");
      setSelected(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteUnit = async (u) => {
    if (!window.confirm(`Hapus unit ${u.block}-${u.number}? Unit dengan booking aktif tidak dapat dihapus.`)) return;
    try { await api.delete(`/sales/units/${u.id}`); toast.success("Unit dihapus"); setSelected(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const uploadSiteplan = (e) => {
    const file = e.target.files[0];
    if (!file || !petaDetail) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = async () => {
        const canvas = document.createElement("canvas");
        const scale = Math.min(1, 1600 / img.width);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        const url = petaBlockDetail ? `/blocks/${petaBlockDetail.id}/siteplan` : `/locations/${petaDetail.id}/siteplan`;
        try {
          await api.put(url, { image: canvas.toDataURL("image/jpeg", 0.82) });
          toast.success("Siteplan diunggah");
          if (petaBlockDetail) {
            const r = await api.get(`/blocks/${petaBlockDetail.id}`);
            setPetaBlockDetail(r.data);
          } else {
            const r = await api.get(`/locations/${petaDetail.id}`);
            setPetaDetail(r.data);
          }
        } catch (err) { toast.error(errMsg(err)); }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const savePolygon = async (uid, pts) => {
    try {
      await api.put(`/sales/units/${uid}/polygon`, { points: pts });
      toast.success("Area poligon unit tersimpan");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const blocks = [...new Set(units.map((u) => u.block))].sort();
  const filtered = filter === "semua" ? units : units.filter((u) => u.status === filter);

  const genSchedules = (method, price, dp) => {
    const sisa = price - dp;
    if (method === "cash") return [{ label: "Pelunasan Cash", due_date: today(), amount: Math.max(sisa, 0) }];
    const n = method === "cash_bertahap" ? 12 : 0;
    if (n === 0) return [];
    const per = Math.round(sisa / n);
    return Array.from({ length: n }, (_, i) => ({
      label: `Cicilan ke-${i + 1}`,
      due_date: new Date(Date.now() + (i + 1) * 30 * 86400000).toISOString().slice(0, 10),
      amount: per,
    }));
  };

  const openBooking = (unit) => {
    setSelected(unit);
    setBk({ customer_name: "", customer_phone: "", price: unit.price, booking_fee: 10000000, dp: Math.round(unit.price * 0.2), payment_method: "cash", schedules: [] });
    setShowBooking(true);
  };

  useEffect(() => {
    if (showBooking && bk.payment_method !== "kpr") {
      setBk((b) => ({ ...b, schedules: genSchedules(b.payment_method, b.price, b.dp) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bk.payment_method, bk.dp, bk.price, showBooking]);

  const saveBooking = async () => {
    try {
      const res = await api.post("/sales/bookings", { ...bk, discount_pct: discountPct, unit_id: selected.id, schedules: bk.payment_method === "kpr" ? [{ label: "Pelunasan via KPR", due_date: today(), amount: Math.max(bk.price - bk.dp, 0) }] : bk.schedules });
      if (res.data?.status === "pending_approval") {
        toast.info(res.data.message);
      } else {
        toast.success("Booking berhasil dibuat");
      }
      setShowBooking(false);
      setDiscountPct(0);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const savePayment = async () => {
    try {
      await api.post(`/sales/bookings/${showPay.id}/payments`, pay);
      toast.success("Pembayaran dicatat & jurnal otomatis dibuat");
      setShowPay(null);
      setPay({ date: today(), amount: 0, kind: "booking_fee", schedule_idx: null, method: "transfer" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveUnit = async () => {
    try {
      await api.post("/sales/units", { ...unitForm, block: unitBlocks.find((b) => b.id === unitForm.block_id)?.name || "" });
      toast.success("Unit ditambahkan");
      setShowUnit(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const onUnitLocationChange = (locId) => {
    setUnitForm((f) => ({ ...f, location_id: locId, block_id: "" }));
    if (locId) api.get(`/locations/${locId}/blocks`).then((r) => setUnitBlocks(r.data)).catch(() => {});
  };

  const cancelBooking = async (id) => {
    if (!window.confirm("Batalkan booking ini? Unit kembali tersedia.")) return;
    try { await api.put(`/sales/bookings/${id}/cancel`); toast.success("Booking dibatalkan"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="penjualan-page">
      <PageHeader title="Penjualan & Siteplan" subtitle="Siteplan interaktif, booking fee, DP, dan jadwal pembayaran" testid="penjualan-header">
        {hasPerm("penjualan", "create") && <Btn tone="ghost" onClick={() => setShowUnit(true)} data-testid="add-unit-button"><Plus className="w-4 h-4" /> Tambah Unit</Btn>}
        {hasPerm("penjualan", "edit") && <Btn tone="ghost" onClick={openLocInfo} data-testid="loc-info-button"><Upload className="w-4 h-4" /> Info & Foto Proyek</Btn>}
      </PageHeader>

      <div className="flex gap-2 mb-5 flex-wrap">
        {[["siteplan", "Siteplan"], ["peta", "Peta Siteplan (JPG)"], ["booking", "Booking & Cicilan"], ["pembayaran", "Riwayat Pembayaran"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`text-sm px-4 py-2 rounded-lg border transition-colors ${tab === k ? "bg-emerald-600/10 text-emerald-300 border-emerald-500/50" : "text-slate-400 border-slate-800 hover:bg-slate-900 hover:text-slate-200"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "siteplan" && (
        <div>
          <div className="flex gap-2 mb-4 flex-wrap" data-testid="siteplan-filter">
            {["semua", "tersedia", "booking", "terjual", "dibangun"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === f ? "bg-slate-800 text-white border-slate-600" : "text-slate-500 border-slate-800 hover:text-slate-300"}`}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          {blocks.map((block) => (
            <Card key={block} className="mb-4" testid={`siteplan-block-${block}`}>
              <h3 className="text-sm font-mono uppercase tracking-wider text-slate-400 mb-3">Blok {block}</h3>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                {filtered.filter((u) => u.block === block).map((u) => {
                  const colors = { tersedia: "border-emerald-500/50 bg-emerald-500/10 hover:bg-emerald-500/20", booking: "border-amber-500/50 bg-amber-500/10 hover:bg-amber-500/20", terjual: "border-rose-500/50 bg-rose-500/10", hold: "border-indigo-500/50 bg-indigo-500/10" };
                  return (
                    <button
                      key={u.id}
                      onClick={() => selectUnit(u)}
                      data-testid={`siteplan-unit-${u.block}${u.number}`}
                      className={`border rounded-lg p-2.5 text-left transition-colors ${colors[u.status] || colors.tersedia} ${u.status === "terjual" ? "opacity-60" : "cursor-pointer"}`}
                    >
                      <div className="font-mono text-sm font-semibold text-slate-100">{u.block}-{u.number}</div>
                      <div className="text-[10px] text-slate-400">{u.type}</div>
                      <div className="text-[10px] font-mono text-slate-300 mt-1">{fmtIDR(u.price)}</div>
                    </button>
                  );
                })}
              </div>
            </Card>
          ))}
          <div className="flex gap-3 flex-wrap mt-2">
            <Badge tone="emerald">Tersedia: {units.filter((u) => u.status === "tersedia").length}</Badge>
            <Badge tone="amber">Booking: {units.filter((u) => u.status === "booking").length}</Badge>
            <Badge tone="rose">Terjual: {units.filter((u) => u.status === "terjual").length}</Badge>
            <Badge tone="indigo">Dibangun: {units.filter((u) => u.status === "dibangun").length}</Badge>
          </div>
        </div>
      )}

      {tab === "peta" && (
        <div data-testid="peta-siteplan-tab">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 max-w-xl">
            <Field label="Lokasi Perumahan">
              <select className={inputCls} value={activePetaProject} onChange={(e) => { setPetaProject(e.target.value); setPetaBlockId(""); }} data-testid="peta-project-select">
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Blok (opsional — siteplan khusus blok)">
              <select className={inputCls} value={petaBlockId} onChange={(e) => setPetaBlockId(e.target.value)} data-testid="peta-block-select">
                <option value="">Semua Blok (siteplan lokasi)</option>
                {petaBlocks.map((b) => <option key={b.id} value={b.id}>Blok {b.name}</option>)}
              </select>
            </Field>
          </div>
          {petaImage ? (
            <SiteplanMap
              image={petaImage}
              units={units.filter((u) => u.project_id === petaDetail?.id && (!petaBlockId || u.block_id === petaBlockId))}
              editable={hasPerm("penjualan", "edit")}
              onSavePolygon={savePolygon}
              onUnitClick={(u) => selectUnit(u)}
            />
          ) : (
            <Card className="text-center py-10" testid="peta-empty">
              <p className="text-slate-400 text-sm mb-4">Belum ada gambar siteplan untuk {petaBlockDetail ? `blok ${petaBlockDetail.name}` : "lokasi"} ini. Unggah denah .jpg lalu gambar area poligon per unit.</p>
              {hasPerm("proyek", "edit") && (
                <>
                  <input ref={siteplanFileRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={uploadSiteplan} data-testid="siteplan-file-input" />
                  <Btn onClick={() => siteplanFileRef.current.click()} data-testid="upload-siteplan-button"><Upload className="w-4 h-4" /> Unggah Siteplan (.jpg)</Btn>
                </>
              )}
            </Card>
          )}
        </div>
      )}

      {tab === "booking" && (
        <DataTable
          testid="bookings-table"
          searchKeys={["customer_name", "number", "unit_label"]}
          columns={[
            { header: "No. Booking", accessor: "number", mono: true },
            { header: "Unit", render: (r) => <span className="font-mono text-xs">{r.unit_label} ({r.unit_type})</span> },
            { header: "Pelanggan", accessor: "customer_name" },
            { header: "Harga", render: (r) => <span className="font-mono text-xs">{fmtIDR(r.price)}</span> },
            { header: "Cara Bayar", render: (r) => <Badge tone="cyan">{r.payment_method}</Badge> },
            { header: "Terbayar", render: (r) => <span className="font-mono text-xs text-emerald-400">{fmtIDR(r.total_paid)}</span> },
            { header: "Status", render: (r) => <Badge tone={statusTone(r.status)}>{r.status}</Badge> },
            { header: "Aksi", render: (r) => r.status !== "batal" && r.status !== "lunas" && hasPerm("penjualan", "create") ? (
              <div className="flex gap-1.5">
                <Btn className="!px-2.5 !py-1 !text-xs" onClick={() => { setShowPay(r); setPay({ ...pay, amount: 0, kind: r.booking_fee_paid < r.booking_fee ? "booking_fee" : "schedule", schedule_idx: r.schedules.findIndex((s) => s.status !== "lunas") }); }} data-testid={`pay-button-${r.number}`}><Banknote className="w-3.5 h-3.5" /> Bayar</Btn>
                {hasPerm("penjualan", "edit") && <Btn tone="amber" className="!px-2.5 !py-1 !text-xs" onClick={() => createAkun(r)} data-testid={`akun-konsumen-${r.number}`}>Akun</Btn>}
                {hasPerm("penjualan", "edit") && <Btn tone="ghost" className="!px-2.5 !py-1 !text-xs" onClick={() => setShowBerkas(r)} data-testid={`berkas-${r.number}`}>Berkas</Btn>}
                {hasPerm("penjualan", "edit") && <Btn tone="danger" className="!px-2.5 !py-1 !text-xs" onClick={() => cancelBooking(r.id)} data-testid={`cancel-booking-${r.number}`}>Batal</Btn>}
                {hasPerm("penjualan", "edit") && (
                  <select className="bg-slate-950 border border-slate-700 rounded px-1.5 py-1 text-[10px] text-slate-300" value={r.process_stage || "booking"}
                    onChange={(e) => setProcess(r.id, e.target.value)} data-testid={`process-${r.number}`}>
                    {[["booking","Booking"],["berkas","Berkas Lengkap"],["ke_bank","Berkas ke Bank"],["approved_bank","Approve Bank"],["spk","SPK Terbit"],["akad_notice","Pemberitahuan Akad"],["dp_lunas","DP Lunas"],["akad","Akad"],["serah_terima","Serah Terima Kunci"],["selesai","Selesai"]].map(([k,l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                )}
              </div>
            ) : null },
          ]}
          rows={bookings}
          empty="Belum ada booking"
        />
      )}

      {tab === "pembayaran" && (
        <DataTable
          testid="payments-table"
          searchKeys={["customer_name", "number", "unit_label"]}
          columns={[
            { header: "No. Kwitansi", accessor: "number", mono: true },
            { header: "Tanggal", render: (r) => fmtDate(r.date) },
            { header: "Pelanggan", accessor: "customer_name" },
            { header: "Unit", accessor: "unit_label", mono: true },
            { header: "Keterangan", accessor: "label" },
            { header: "Jumlah", render: (r) => <span className="font-mono text-xs text-emerald-400">{fmtIDR(r.amount)}</span> },
          ]}
          rows={payments}
          empty="Belum ada pembayaran"
        />
      )}

      <Modal open={!!selected && !showBooking} onClose={() => setSelected(null)} title={selected ? `Unit ${selected.block}-${selected.number}` : ""} testid="unit-detail-modal">
        {selected && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><span className="text-slate-500 text-xs">Tipe</span><div className="text-slate-200">{selected.type}</div></div>
              <div><span className="text-slate-500 text-xs">Status</span><div><Badge tone={statusTone(selected.status)}>{selected.status}</Badge></div></div>
              <div><span className="text-slate-500 text-xs">Luas Tanah / Bangunan</span><div className="text-slate-200">{selected.luas_tanah}m² / {selected.luas_bangunan}m²</div></div>
              <div><span className="text-slate-500 text-xs">Kamar Tidur / Mandi</span><div className="text-slate-200">{selected.bedrooms ?? 2} KT / {selected.bathrooms ?? 1} KM</div></div>
              <div><span className="text-slate-500 text-xs">Harga</span><div className="font-mono text-emerald-400">{fmtIDR(selected.price)}</div></div>
            </div>
            {selected.status === "tersedia" && hasPerm("penjualan", "create") && (
              <Btn onClick={() => openBooking(selected)} data-testid="unit-book-button">Booking Unit Ini</Btn>
            )}
            {hasPerm("penjualan", "edit") && (
              <div className="border-t border-slate-800 pt-3 space-y-3" data-testid="unit-admin-edit">
                <div className="text-xs font-mono uppercase tracking-wider text-slate-400">Penyesuaian Admin — berlaku juga untuk unit terjual</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Status Unit">
                    <select className={inputCls} value={unitEditForm.status} onChange={(e) => setUnitEditForm({ ...unitEditForm, status: e.target.value })} data-testid="unit-status-select">
                      {["tersedia", "booking", "terjual", "hold", "dibangun"].map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                    </select>
                  </Field>
                  <Field label="Nomor Unit"><input className={inputCls} value={unitEditForm.number} onChange={(e) => setUnitEditForm({ ...unitEditForm, number: e.target.value })} data-testid="unit-edit-number" /></Field>
                  <Field label="Tipe"><input className={inputCls} value={unitEditForm.type} onChange={(e) => setUnitEditForm({ ...unitEditForm, type: e.target.value })} data-testid="unit-edit-type" /></Field>
                  <Field label="Harga (Rp)"><input type="number" className={inputCls} value={unitEditForm.price} onChange={(e) => setUnitEditForm({ ...unitEditForm, price: +e.target.value })} data-testid="unit-edit-price" /></Field>
                </div>
                <div className="flex justify-between gap-2">
                  {hasPerm("penjualan", "delete")
                    ? <Btn tone="danger" className="!text-xs" onClick={() => deleteUnit(selected)} data-testid="unit-delete-button">Hapus Unit</Btn>
                    : <span />}
                  <Btn onClick={saveUnitEdit} data-testid="unit-edit-save-button">Simpan Perubahan</Btn>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal open={showBooking} onClose={() => setShowBooking(false)} title={`Booking Unit ${selected?.block}-${selected?.number}`} wide testid="booking-form-modal">
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Nama Pembeli"><input className={inputCls} value={bk.customer_name} onChange={(e) => setBk({ ...bk, customer_name: e.target.value })} data-testid="booking-name-input" /></Field>
            <Field label="Telepon"><input className={inputCls} value={bk.customer_phone} onChange={(e) => setBk({ ...bk, customer_phone: e.target.value })} data-testid="booking-phone-input" /></Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Harga Jual"><input type="number" className={inputCls} value={bk.price} onChange={(e) => setBk({ ...bk, price: +e.target.value })} data-testid="booking-price-input" /></Field>
            <Field label="Booking Fee"><input type="number" className={inputCls} value={bk.booking_fee} onChange={(e) => setBk({ ...bk, booking_fee: +e.target.value })} data-testid="booking-fee-input" /></Field>
            <Field label="Uang Muka (DP)"><input type="number" className={inputCls} value={bk.dp} onChange={(e) => setBk({ ...bk, dp: +e.target.value })} data-testid="booking-dp-input" /></Field>
          </div>
          <Field label="Cara Bayar">
            <select className={inputCls} value={bk.payment_method} onChange={(e) => setBk({ ...bk, payment_method: e.target.value })} data-testid="booking-method-select">
              <option value="cash">Cash Keras</option>
              <option value="cash_bertahap">Cash Bertahap (12x)</option>
              <option value="kpr">KPR</option>
            </select>
          </Field>
          <Field label="Diskon (%) — di atas ambang butuh approval">
            <input type="number" min="0" max="100" step="0.5" className={inputCls} value={discountPct}
              onChange={(e) => { const v = +e.target.value; setDiscountPct(v); if (selected) setBk((b) => ({ ...b, price: Math.round(selected.price * (1 - v / 100)) })); }}
              data-testid="booking-discount-input" />
          </Field>
          <div>
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-slate-400 mb-2"><CalendarClock className="w-3.5 h-3.5" /> Jadwal Pembayaran</div>
            <div className="max-h-44 overflow-y-auto space-y-1">
              {(bk.payment_method === "kpr" ? [{ label: "Pelunasan via KPR", due_date: today(), amount: Math.max(bk.price - bk.dp, 0) }] : bk.schedules).map((s, i) => (
                <div key={i} className="flex justify-between text-xs bg-slate-950 border border-slate-800 rounded-lg px-3 py-2">
                  <span className="text-slate-300">{s.label}</span>
                  <span className="text-slate-500">{fmtDate(s.due_date)}</span>
                  <span className="font-mono text-emerald-400">{fmtIDR(s.amount)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Btn tone="ghost" onClick={() => setShowBooking(false)}>Batal</Btn>
            <Btn onClick={saveBooking} data-testid="booking-save-button">Buat Booking</Btn>
          </div>
        </div>
      </Modal>

      <Modal open={!!showPay} onClose={() => setShowPay(null)} title={showPay ? `Pembayaran - ${showPay.customer_name} (${showPay.unit_label})` : ""} testid="payment-form-modal">
        {showPay && (
          <div className="space-y-3">
            <div className="text-xs text-slate-400">Booking Fee: <span className="font-mono text-slate-200">{fmtIDR(showPay.booking_fee_paid)}/{fmtIDR(showPay.booking_fee)}</span></div>
            <Field label="Jenis Pembayaran">
              <select className={inputCls} value={pay.kind} onChange={(e) => setPay({ ...pay, kind: e.target.value })} data-testid="payment-kind-select">
                <option value="booking_fee">Booking Fee</option>
                <option value="schedule">Cicilan / Pelunasan</option>
              </select>
            </Field>
            {pay.kind === "schedule" && (
              <Field label="Jadwal">
                <select className={inputCls} value={pay.schedule_idx ?? ""} onChange={(e) => setPay({ ...pay, schedule_idx: +e.target.value })} data-testid="payment-schedule-select">
                  {showPay.schedules.map((s, i) => (
                    <option key={i} value={i} disabled={s.status === "lunas"}>{s.label} - {fmtIDR(s.amount)} ({s.status})</option>
                  ))}
                </select>
              </Field>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Tanggal"><input type="date" className={inputCls} value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} data-testid="payment-date-input" /></Field>
              <Field label="Jumlah (Rp)"><input type="number" className={inputCls} value={pay.amount} onChange={(e) => setPay({ ...pay, amount: +e.target.value })} data-testid="payment-amount-input" /></Field>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Btn tone="ghost" onClick={() => setShowPay(null)}>Batal</Btn>
              <Btn onClick={savePayment} data-testid="payment-save-button">Catat & Jurnal</Btn>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!locInfo} onClose={() => setLocInfo(null)} title="Info & Foto Proyek (Katalog ABAD JAYA LIVING)" testid="loc-info-modal">
        {locInfo && (
          <div className="space-y-3">
            <Field label="Deskripsi Proyek"><textarea className={inputCls} rows={3} value={locInfo.description} onChange={(e) => setLocInfo({ ...locInfo, description: e.target.value })} data-testid="loc-desc-input" /></Field>
            <Field label="Fasilitas (pisahkan dengan koma)"><input className={inputCls} value={locInfo.facilities} onChange={(e) => setLocInfo({ ...locInfo, facilities: e.target.value })} placeholder="Masjid, Taman, One Gate System" data-testid="loc-facilities-input" /></Field>
            <div>
              <span className="text-xs font-mono uppercase tracking-wider text-slate-400 block mb-1.5">Tempat Terdekat</span>
              {locInfo.nearby.map((n, i) => (
                <div key={i} className="flex gap-2 mb-1.5">
                  <input className={inputCls} placeholder="Nama (cth: RSUD Stabat)" value={n.name} onChange={(e) => { const nb = [...locInfo.nearby]; nb[i] = { ...nb[i], name: e.target.value }; setLocInfo({ ...locInfo, nearby: nb }); }} data-testid={`loc-nearby-name-${i}`} />
                  <input className={`${inputCls} !w-32`} placeholder="2 km" value={n.distance} onChange={(e) => { const nb = [...locInfo.nearby]; nb[i] = { ...nb[i], distance: e.target.value }; setLocInfo({ ...locInfo, nearby: nb }); }} data-testid={`loc-nearby-dist-${i}`} />
                </div>
              ))}
              <Btn tone="ghost" className="!text-xs" onClick={() => setLocInfo({ ...locInfo, nearby: [...locInfo.nearby, { name: "", distance: "" }] })} data-testid="loc-nearby-add">+ Tambah Tempat</Btn>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Latitude"><input type="number" step="any" className={inputCls} value={locInfo.lat} onChange={(e) => setLocInfo({ ...locInfo, lat: e.target.value })} data-testid="loc-lat-input" /></Field>
              <Field label="Longitude"><input type="number" step="any" className={inputCls} value={locInfo.lng} onChange={(e) => setLocInfo({ ...locInfo, lng: e.target.value })} data-testid="loc-lng-input" /></Field>
            </div>
            <div>
              <span className="text-xs font-mono uppercase tracking-wider text-slate-400 block mb-1.5">Foto Marketing Proyek ({locInfo.images.length} terunggah)</span>
              <input ref={locPhotoRef} type="file" accept="image/*" multiple className="hidden" onChange={uploadLocPhotos} data-testid="loc-photos-input" />
              <Btn tone="ghost" onClick={() => locPhotoRef.current.click()} data-testid="loc-photos-upload"><Upload className="w-4 h-4" /> Upload Foto Proyek</Btn>
            </div>
            <div className="flex justify-end gap-2"><Btn tone="ghost" onClick={() => setLocInfo(null)}>Batal</Btn><Btn onClick={saveLocInfo} data-testid="loc-info-save">Simpan Info Proyek</Btn></div>
          </div>
        )}
      </Modal>

      <Modal open={showUnit} onClose={() => setShowUnit(false)} title="Tambah Unit" testid="unit-form-modal">
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Lokasi Perumahan">
              <select className={inputCls} value={unitForm.location_id} onChange={(e) => onUnitLocationChange(e.target.value)} data-testid="unit-location-select">
                <option value="">Pilih lokasi...</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Blok Rumah">
              <select className={inputCls} value={unitForm.block_id} onChange={(e) => setUnitForm({ ...unitForm, block_id: e.target.value })} data-testid="unit-block-select">
                <option value="">Pilih blok...</option>
                {unitBlocks.map((b) => <option key={b.id} value={b.id}>Blok {b.name}</option>)}
              </select>
            </Field>
            <Field label="Nomor Unit"><input className={inputCls} value={unitForm.number} onChange={(e) => setUnitForm({ ...unitForm, number: e.target.value })} data-testid="unit-number-input" /></Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Tipe"><input className={inputCls} value={unitForm.type} onChange={(e) => setUnitForm({ ...unitForm, type: e.target.value })} data-testid="unit-type-input" /></Field>
            <Field label="Luas Tanah"><input type="number" className={inputCls} value={unitForm.luas_tanah} onChange={(e) => setUnitForm({ ...unitForm, luas_tanah: +e.target.value })} /></Field>
            <Field label="Luas Bangunan"><input type="number" className={inputCls} value={unitForm.luas_bangunan} onChange={(e) => setUnitForm({ ...unitForm, luas_bangunan: +e.target.value })} /></Field>
            <Field label="Kamar Tidur"><input type="number" className={inputCls} value={unitForm.bedrooms} onChange={(e) => setUnitForm({ ...unitForm, bedrooms: +e.target.value })} data-testid="unit-bedrooms-input" /></Field>
            <Field label="Kamar Mandi"><input type="number" className={inputCls} value={unitForm.bathrooms} onChange={(e) => setUnitForm({ ...unitForm, bathrooms: +e.target.value })} data-testid="unit-bathrooms-input" /></Field>
          </div>
          <Field label="Harga (Rp)"><input type="number" className={inputCls} value={unitForm.price} onChange={(e) => setUnitForm({ ...unitForm, price: +e.target.value })} data-testid="unit-price-input" /></Field>
          <div className="flex justify-end gap-2 pt-2">
            <Btn tone="ghost" onClick={() => setShowUnit(false)}>Batal</Btn>
            <Btn onClick={saveUnit} data-testid="unit-save-button">Simpan</Btn>
          </div>
        </div>
      </Modal>
      <Modal open={!!showBerkas} onClose={() => setShowBerkas(null)} title={showBerkas ? `Berkas Konsumen — ${showBerkas.customer_name}` : ""} testid="berkas-modal">
        {showBerkas && (
          <div className="space-y-3">
            <p className="text-xs text-slate-400">Unggah softcopy berkas untuk persyaratan bank. KTP Pasangan wajib bila konsumen sudah menikah.</p>
            {[["ktp_pemohon", "KTP Pemohon"], ["ktp_pasangan", `KTP Pasangan${showBerkas.menikah ? " (wajib — menikah)" : " (opsional)"}`], ["kk", "Kartu Keluarga"], ["npwp", "NPWP"]].map(([k, label]) => {
              const up = showBerkas.berkas?.[k];
              return (
                <div key={k} className="flex items-center justify-between bg-slate-950 border border-slate-800 rounded-lg px-3 py-2.5" data-testid={`berkas-row-${k}`}>
                  <span className="text-xs text-slate-300">{label}</span>
                  {up ? (
                    <span className="text-[10px] font-mono text-emerald-400">Terunggah ✓ {fmtDate((up.at || "").slice(0, 10))}</span>
                  ) : (
                    <label className="text-[10px] text-cyan-300 border border-cyan-500/40 rounded px-2 py-1 cursor-pointer hover:bg-cyan-500/10" data-testid={`berkas-upload-${k}`}>
                      Unggah
                      <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => uploadBerkas(k, e)} />
                    </label>
                  )}
                </div>
              );
            })}
            <div className="flex justify-end"><Btn onClick={() => setShowBerkas(null)}>Tutup</Btn></div>
          </div>
        )}
      </Modal>
      <Modal open={!!akunRes} onClose={() => setAkunRes(null)} title="Akun Portal Konsumen" testid="akun-modal">
        {akunRes && (
          <div className="space-y-3">
            <p className="text-xs text-slate-400">{akunRes.message}</p>
            <div className="bg-slate-950 border border-slate-800 rounded-lg p-4 space-y-2 font-mono text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Konsumen</span><span className="text-slate-200">{akunRes.customer}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Email</span><span className="text-emerald-400" data-testid="akun-email">{akunRes.email}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Password</span><span className="text-emerald-400" data-testid="akun-password">{akunRes.password || "(sudah dibuat sebelumnya)"}</span></div>
            </div>
            <p className="text-[11px] text-slate-500">Konsumen login melalui halaman login yang sama — otomatis diarahkan ke portal perkembangan pemesanannya. Simpan password ini, hanya ditampilkan sekali.</p>
            <div className="flex justify-end"><Btn onClick={() => setAkunRes(null)} data-testid="akun-close">Tutup</Btn></div>
          </div>
        )}
      </Modal>
    </div>
  );
}
